import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'

// MUI Imports
import {
  Dialog,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tab,
  Tabs,
  Box
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

// Component Imports
import DialogCloseButton from '../atoms/DialogCloseButton'
import ProfileAutofillTabContent from './ProfileAutofillTabContent'
import EmailSettingsTabContent from './EmailSettingsTabContent'
import EmailSettingsTestButtons from '../molecules/EmailSettingsTestButtons'
import DeleteProfileConfirmDialog from '../molecules/DeleteProfileConfirmDialog'

// Service Imports
import { updateUser, scrapeEmailGeneratorParameters } from '../../../services/B2BServices'
import { getUserDetailsAndProcess } from '../../../../../services/DataServiceMethods'

// Hook Imports
import { useToast } from '../../../../../campaigns/hooks/useToast'
import { useProfileManagement } from '../../../hooks/useProfileManagement'
import { useEmailSettingsTests } from '../../../hooks/useEmailSettingsTests'

// Store Imports
import {
  useGlobalStoreActions,
  useGlobalStoreData
} from '../../../../../store/globalStore'

// Data Imports
import { countryEN } from '../../../../../assets/data/country-en'
import { countryTR } from '../../../../../assets/data/country-tr'
import { TRADE_TYPES, LANGUAGES } from '../../../utils/campaignConstants'

// Utility Imports
import {
  extractSenderCompanyData,
  extractEmailPurposeData,
  filterEmptyValues,
  createFormValuesFromProfile
} from '../../../utils/profileDataTransformers'

const EditUserEmailSettingsModal = (props) => {
  const {
    userEditState,
    close,
    userGSD,
    open
  } = props
  const { t } = useTranslation()

  const { showError, showSuccess } = useToast()
  const { setUserData } = useGlobalStoreActions()

  const languageGSD = useGlobalStoreData.language()

  const [loading, setLoading] = useState(false)
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [activeTab, setActiveTab] = useState('1')
  const [connectionType, setConnectionType] = useState(
    userEditState?.emailSettings?.accountType === 'GMAIL_OAUTH2' ? 'gmail' : 'manual'
  )

  const [profiles, setProfiles] = useState([])
  const [selectedProfileIndex, setSelectedProfileIndex] = useState(0)

  const [profileToDelete, setProfileToDelete] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const [websiteUrl, setWebsiteUrl] = useState('')
  const [isFetchingFromWebsite, setIsFetchingFromWebsite] = useState(false)

  const initialEmailSettingsRef = useRef(null)

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm()

  const watchedValues = watch()

  const handleChange = useCallback((_event, newValue) => {
    setActiveTab(newValue)
  }, [])

  const companyValidationRules = useMemo(() => ({
    required: { required: 'This field is required' },
    email: {
      required: 'Email is required',
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'Invalid email address'
      }
    },
    keyProducts: {
      validate: (value) => {
        if (!value || value.length === 0) {
          return 'At least one key product is required'
        }
        return true
      }
    }
  }), [])

  const getLocalizedCountryName = useCallback((countryName) => {
    if (!countryName) return ''

    const currentCountries = languageGSD === 'en' ? countryEN : countryTR
    const otherCountries = languageGSD === 'en' ? countryTR : countryEN

    const foundInCurrent = currentCountries.find(c => c.countryName === countryName)
    if (foundInCurrent) {
      return countryName
    }

    const foundInOther = otherCountries.find(c => c.countryName === countryName)
    if (foundInOther) {
      const translatedCountry = currentCountries.find(c => c.countryCode === foundInOther.countryCode)
      if (translatedCountry) {
        return translatedCountry.countryName
      }
    }

    return countryName
  }, [languageGSD])

  const {
    isAddingNewProfile,
    newProfileName,
    handleProfileChange,
    handleStartAddProfile: originalHandleStartAddProfile,
    handleAddNewProfile: originalHandleAddNewProfile,
    handleCancelAddProfile: originalHandleCancelAddProfile,
    handleProfileNameChange
  } = useProfileManagement(
    profiles,
    setProfiles,
    selectedProfileIndex,
    setSelectedProfileIndex,
    watch,
    reset,
    getLocalizedCountryName,
    userEditState
  )

  const handleStartAddProfileWrapper = useCallback(() => {
    setWebsiteUrl('')
    originalHandleStartAddProfile()
  }, [originalHandleStartAddProfile])

  const handleCancelAddProfileWrapper = useCallback(() => {
    setWebsiteUrl('')
    originalHandleCancelAddProfile()
  }, [originalHandleCancelAddProfile])

  const handleAddNewProfileWrapper = useCallback(() => {
    setWebsiteUrl('')
    originalHandleAddNewProfile()
  }, [originalHandleAddNewProfile])

  const {
    testingSender,
    testingReceiver,
    isTestSenderDisabled,
    isTestReceiverDisabled,
    testSenderSettings,
    testReceiverSettings
  } = useEmailSettingsTests(
    connectionType,
    userEditState,
    watchedValues
  )

  const handleClose = useCallback(() => {
    reset()
    if (isAddingNewProfile) {
      originalHandleCancelAddProfile()
    }
    setWebsiteUrl('')
    close()
  }, [reset, close, isAddingNewProfile, originalHandleCancelAddProfile])

  const handleClickShowPassword = useCallback(() => {
    setIsPasswordShown((show) => !show)
  }, [])

  const handleConnectionTypeChange = useCallback((event) => {
    setConnectionType(event.target.value)
  }, [])

  const handleDeleteProfile = useCallback((index) => {
    if (profiles.length <= 1) {
      showError(t('mailTemplate.users.modal.cannotDeleteLastProfile'))
      return
    }

    setProfileToDelete(index)
    setDeleteConfirmOpen(true)
  }, [profiles.length, showError, t])

  const handleConfirmDelete = useCallback(() => {
    if (profileToDelete === null) return

    const updatedProfiles = profiles.filter((_, i) => i !== profileToDelete)

    const payload = {
      emailGeneratorParameters: updatedProfiles
    }

    setLoading(true)
    updateUser(userEditState?.id, payload).then(() => {
      getUserDetailsAndProcess(true, userGSD, setUserData, true)
      showSuccess(t('mailTemplate.users.modal.profileDeleted'))
      setProfiles(updatedProfiles)

      if (selectedProfileIndex >= updatedProfiles.length) {
        handleProfileChange(updatedProfiles.length - 1)
      } else if (selectedProfileIndex === profileToDelete) {
        handleProfileChange(0)
      }
    }).catch((err) => {
      err && showError(t('mailTemplate.users.modal.profileDeleteFailed'))
    }).finally(() => {
      setLoading(false)
      setDeleteConfirmOpen(false)
      setProfileToDelete(null)
    })
  }, [profileToDelete, profiles, selectedProfileIndex, handleProfileChange, showError, showSuccess, t, userEditState?.id, userGSD, setUserData])

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false)
    setProfileToDelete(null)
  }, [])

  const handleWebsiteUrlChange = useCallback((url) => {
    setWebsiteUrl(url)
  }, [])

  const handleFetchFromWebsite = useCallback(async () => {
    if (!websiteUrl.trim()) {
      showError(t('mailTemplate.users.modal.websiteUrlRequired') || 'Please enter a website URL')
      return
    }

    setIsFetchingFromWebsite(true)
    try {
      const response = await scrapeEmailGeneratorParameters(websiteUrl.trim())
      const scrapedData = response.data

      if (scrapedData) {
        const localizedCountry = scrapedData.sender_company?.country
          ? getLocalizedCountryName(scrapedData.sender_company.country)
          : watchedValues['senderCompany.country']

        const localizedTargetMarket = scrapedData.email_purpose?.target_market
          ? getLocalizedCountryName(scrapedData.email_purpose.target_market)
          : watchedValues['emailPurpose.target_market']

        const updatedSenderCompany = {
          name: scrapedData.sender_company?.name ?? watchedValues['senderCompany.name'],
          industry: scrapedData.sender_company?.industry ?? watchedValues['senderCompany.industry'],
          country: localizedCountry,
          contact_person: scrapedData.sender_company?.contact_person ?? watchedValues['senderCompany.contact_person'],
          contact_title: scrapedData.sender_company?.contact_title ?? watchedValues['senderCompany.contact_title'],
          email: scrapedData.sender_company?.email ?? watchedValues['senderCompany.email'],
          key_products: scrapedData.sender_company?.key_products ?? watchedValues['senderCompany.key_products'],
          website: scrapedData.sender_company?.website ?? watchedValues['senderCompany.website'],
          phone: scrapedData.sender_company?.phone ?? watchedValues['senderCompany.phone'],
          production_capacity: scrapedData.sender_company?.production_capacity ?? watchedValues['senderCompany.production_capacity'],
          facilities: scrapedData.sender_company?.facilities ?? watchedValues['senderCompany.facilities'],
          certifications: scrapedData.sender_company?.certifications ?? watchedValues['senderCompany.certifications'],
          market_experience: scrapedData.sender_company?.market_experience ?? watchedValues['senderCompany.market_experience'],
          company_values: scrapedData.sender_company?.company_values ?? watchedValues['senderCompany.company_values']
        }

        const updatedEmailPurpose = {
          target_market: localizedTargetMarket,
          trade_type: scrapedData.email_purpose?.trade_type ?? watchedValues['emailPurpose.trade_type'],
          product_category: scrapedData.email_purpose?.product_category ?? watchedValues['emailPurpose.product_category'],
          language: scrapedData.email_purpose?.language ?? watchedValues['emailPurpose.language'],
          specific_products: scrapedData.email_purpose?.specific_products ?? watchedValues['emailPurpose.specific_products'],
          call_to_action: scrapedData.email_purpose?.call_to_action ?? watchedValues['emailPurpose.call_to_action']
        }

        const updatedValues = {
          ...watchedValues,
          'senderCompany.name': updatedSenderCompany.name,
          'senderCompany.industry': updatedSenderCompany.industry,
          'senderCompany.country': updatedSenderCompany.country,
          'senderCompany.contact_person': updatedSenderCompany.contact_person,
          'senderCompany.contact_title': updatedSenderCompany.contact_title,
          'senderCompany.email': updatedSenderCompany.email,
          'senderCompany.key_products': updatedSenderCompany.key_products,
          'senderCompany.website': updatedSenderCompany.website,
          'senderCompany.phone': updatedSenderCompany.phone,
          'senderCompany.production_capacity': updatedSenderCompany.production_capacity,
          'senderCompany.facilities': updatedSenderCompany.facilities,
          'senderCompany.certifications': updatedSenderCompany.certifications,
          'senderCompany.market_experience': updatedSenderCompany.market_experience,
          'senderCompany.company_values': updatedSenderCompany.company_values,
          'emailPurpose.target_market': updatedEmailPurpose.target_market,
          'emailPurpose.trade_type': updatedEmailPurpose.trade_type,
          'emailPurpose.product_category': updatedEmailPurpose.product_category,
          'emailPurpose.language': updatedEmailPurpose.language,
          'emailPurpose.specific_products': updatedEmailPurpose.specific_products,
          'emailPurpose.call_to_action': updatedEmailPurpose.call_to_action,
          senderCompany: updatedSenderCompany,
          emailPurpose: updatedEmailPurpose
        }

        reset(updatedValues)
        showSuccess(t('mailTemplate.users.modal.dataFetchedSuccessfully') || 'Data fetched successfully from website')
      }
    } catch (error) {
      showError(t('mailTemplate.users.modal.dataFetchFailed') || 'Failed to fetch data from website. Please check the URL and try again.')
    } finally {
      setIsFetchingFromWebsite(false)
    }
  }, [websiteUrl, watchedValues, reset, showError, showSuccess, t, getLocalizedCountryName])

  const onSubmit = useCallback((val) => {
    const currentEmailSettings = {
      emailUserName: val.emailUserName,
      emailPassword: val.emailPassword,
      smtpHostName: val.smtpHostName,
      smtpPort: Number(val.smtpPort),
      transportStrategy: val.transportStrategy,
      rxHostName: val.rxHostName,
      rxPort: Number(val.rxPort),
      rxProtocol: val.rxProtocol,
      inboxFolderName: val.inboxFolderName
    }

    const normalizedInitialSettings = initialEmailSettingsRef.current
      ? {
          ...initialEmailSettingsRef.current,
          smtpPort: Number(initialEmailSettingsRef.current.smtpPort),
          rxPort: Number(initialEmailSettingsRef.current.rxPort)
        }
      : null

    const hasEmailSettingsChanged = normalizedInitialSettings && !isEqual(currentEmailSettings, normalizedInitialSettings)

    let updatedProfiles = [...profiles]

    if (isAddingNewProfile) {
      if (!newProfileName.trim()) {
        showError(t('mailTemplate.users.modal.profileNameRequired'))
        return
      }

      if (profiles.some(p => p.name === newProfileName.trim())) {
        showError(t('mailTemplate.users.modal.profileNameExists'))
        return
      }

      const senderCompanyData = extractSenderCompanyData(val)
      const emailPurposeData = extractEmailPurposeData(val)

      const filteredSenderCompany = filterEmptyValues(senderCompanyData)
      const filteredEmailPurpose = filterEmptyValues(emailPurposeData)

      const newProfile = {
        name: newProfileName.trim(),
        sender_company: Object.keys(filteredSenderCompany).length > 0 ? filteredSenderCompany : {},
        email_purpose: Object.keys(filteredEmailPurpose).length > 0 ? filteredEmailPurpose : {}
      }

      updatedProfiles = [...profiles, newProfile]
    } else {
      if (updatedProfiles[selectedProfileIndex]) {
        const senderCompanyData = extractSenderCompanyData(val)
        const emailPurposeData = extractEmailPurposeData(val)

        const filteredSenderCompany = filterEmptyValues(senderCompanyData)
        const filteredEmailPurpose = filterEmptyValues(emailPurposeData)

        updatedProfiles[selectedProfileIndex] = {
          ...updatedProfiles[selectedProfileIndex],
          sender_company: Object.keys(filteredSenderCompany).length > 0 ? filteredSenderCompany : {},
          email_purpose: Object.keys(filteredEmailPurpose).length > 0 ? filteredEmailPurpose : {}
        }
      }
    }

    const payload = {
      emailGeneratorParameters: updatedProfiles
    }

    if (hasEmailSettingsChanged) {
      payload.emailSettings = currentEmailSettings
    }

    setLoading(true)
    updateUser(userEditState?.id, payload).then(() => {
      getUserDetailsAndProcess(true, userGSD, setUserData, true)
      showSuccess(t('mailTemplate.users.modal.toastSuccess'))

      if (isAddingNewProfile) {
        handleCancelAddProfileWrapper()
      }
    }).catch((err) => {
      err && showError(t('mailTemplate.users.modal.toastErr'))
    }).finally(() => {
      setLoading(false)
      handleClose()
    })
  }, [
    profiles,
    selectedProfileIndex,
    userEditState?.id,
    userGSD,
    setUserData,
    showError,
    showSuccess,
    t,
    handleClose,
    isAddingNewProfile,
    newProfileName,
    handleCancelAddProfileWrapper
  ])

  useEffect(() => {
    const initialProfiles = userEditState?.emailGeneratorParameters?.length > 0
      ? userEditState.emailGeneratorParameters
      : [
          {
            name: 'default',
            sender_company: userEditState?.senderCompany || {
              name: '',
              industry: '',
              country: '',
              contact_person: '',
              contact_title: '',
              email: userEditState?.emailSettings?.emailUserName || '',
              key_products: [],
              website: '',
              phone: '',
              production_capacity: '',
              facilities: [],
              certifications: [],
              market_experience: [],
              company_values: []
            },
            email_purpose: userEditState?.emailPurpose || {
              target_market: '',
              trade_type: TRADE_TYPES.EXPORT,
              product_category: '',
              language: LANGUAGES.ENGLISH,
              specific_products: [],
              call_to_action: ''
            }
          }]

    setProfiles(initialProfiles)
    setSelectedProfileIndex(0)

    const firstProfile = initialProfiles[0]
    const formValues = createFormValuesFromProfile(
      firstProfile,
      userEditState?.emailSettings,
      getLocalizedCountryName
    )
    reset(formValues)

    setConnectionType(
      userEditState?.emailSettings?.accountType === 'GMAIL_OAUTH2' ? 'gmail' : 'manual'
    )

    initialEmailSettingsRef.current = {
      emailUserName: userEditState?.emailSettings?.emailUserName || '',
      emailPassword: userEditState?.emailSettings?.emailPassword || '',
      smtpHostName: userEditState?.emailSettings?.smtpHostName || '',
      smtpPort: userEditState?.emailSettings?.smtpPort || '',
      transportStrategy: userEditState?.emailSettings?.transportStrategy || '',
      rxHostName: userEditState?.emailSettings?.rxHostName || '',
      rxPort: userEditState?.emailSettings?.rxPort || '',
      rxProtocol: userEditState?.emailSettings?.rxProtocol || '',
      inboxFolderName: userEditState?.emailSettings?.inboxFolderName || ''
    }
  }, [reset, userEditState, getLocalizedCountryName])

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          overflow: 'visible',
          maxHeight: '90vh',
          borderRadius: 3
        }
      }}
    >
      <DialogCloseButton onClick={close} disableRipple>
        <CloseIcon/>
      </DialogCloseButton>
      <DialogTitle
        variant="h4"
        className="flex gap-2 flex-col text-center sm:pbs-16 sm:pbe-6 sm:pli-16"
      >
        {t('mailTemplate.users.modal.subTitle')}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Tabs
          value={activeTab}
          onChange={handleChange}
          aria-label="email settings tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('mailTemplate.wizard.ai.emailGeneratorSettings')} value="1"/>
          <Tab label={t('signIn.mailInfo')} value="2"/>
        </Tabs>
        <DialogContent
          className="pbs-0 sm:pli-16"
          sx={{
            overflow: 'auto',
            maxHeight: '60vh',
            pt: 2,
            pb: 6,
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)'
              }
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent'
          }}
        >
          {activeTab === '1' && (
            <ProfileAutofillTabContent
              control={control}
              companyValidationRules={companyValidationRules}
              profiles={profiles}
              selectedProfileIndex={selectedProfileIndex}
              isAddingNewProfile={isAddingNewProfile}
              onProfileChange={handleProfileChange}
              onAddProfile={handleAddNewProfileWrapper}
              onDeleteProfile={handleDeleteProfile}
              onCancelAdd={handleCancelAddProfileWrapper}
              onStartAdd={handleStartAddProfileWrapper}
              onProfileNameChange={handleProfileNameChange}
              websiteUrl={websiteUrl}
              onWebsiteUrlChange={handleWebsiteUrlChange}
              onFetchFromWebsite={handleFetchFromWebsite}
              isFetchingFromWebsite={isFetchingFromWebsite}
            />
          )}

          {activeTab === '2' && (
            <EmailSettingsTabContent
              control={control}
              errors={errors}
              connectionType={connectionType}
              isPasswordShown={isPasswordShown}
              languageGSD={languageGSD}
              userEditState={userEditState}
              onConnectionTypeChange={handleConnectionTypeChange}
              onTogglePassword={handleClickShowPassword}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 12, py: 3, gap: 1, justifyContent: 'space-between', mb: 4, mt: 2 }}>
          {activeTab === '2' && connectionType === 'manual'
            ? (
              <EmailSettingsTestButtons
                testingSender={testingSender}
                testingReceiver={testingReceiver}
                isTestSenderDisabled={isTestSenderDisabled}
                isTestReceiverDisabled={isTestReceiverDisabled}
                onTestSender={handleSubmit(testSenderSettings)}
                onTestReceiver={handleSubmit(testReceiverSettings)}
              />
              )
            : (
              <Box/>
              )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="inherit"
              type="reset"
              sx={{ borderRadius: 2 }}
            >
              {t('mailTemplate.users.modal.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={loading || (isAddingNewProfile && !newProfileName.trim())}
              startIcon={loading
                ? (
                  <CircularProgress
                    sx={{ color: '#fff' }}
                    size={20}
                  />
                  )
                : null}
              autoFocus
              sx={{ borderRadius: 2 }}
            >
              {t('mailTemplate.wizard.rte.saveBtn')}
            </Button>
          </Box>
        </DialogActions>
      </form>
      <DeleteProfileConfirmDialog
        open={deleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        profileName={profileToDelete !== null ? profiles[profileToDelete]?.name : ''}
      />
    </Dialog>
  )
}

export default EditUserEmailSettingsModal
